import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Download, 
  Star, 
  Shield, 
  Zap, 
  MapPin, 
  Bell, 
  Camera,
  Fingerprint,
  Wifi,
  WifiOff,
  Battery,
  Moon,
  Vibrate,
  Share2,
  Clock,
  Award,
  Gift,
  Crown,
  Sparkles,
  ChevronRight,
  Check,
  X,
  Apple,
  PlayCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const AppStoreListing = () => {
  const [activeTab, setActiveTab] = useState('playstore');
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
  }, []);

  const appFeatures = {
    exclusive: [
      { icon: Fingerprint, title: "Biometric Login", desc: "Face ID & fingerprint authentication" },
      { icon: WifiOff, title: "Offline Mode", desc: "Access saved data without internet" },
      { icon: Vibrate, title: "Haptic Feedback", desc: "Feel every interaction" },
      { icon: Camera, title: "AR Vehicle Scanner", desc: "AI-powered vehicle diagnostics" },
      { icon: Bell, title: "Smart Notifications", desc: "Priority alerts & quiet hours" },
      { icon: Moon, title: "Auto Dark Mode", desc: "Syncs with your device settings" },
      { icon: MapPin, title: "Background GPS", desc: "Continuous location for faster help" },
      { icon: Battery, title: "Battery Saver", desc: "Optimized for all-day use" },
      { icon: Share2, title: "Quick Share", desc: "Share location with one tap" },
      { icon: Clock, title: "Widgets", desc: "Home screen quick actions" },
    ],
    premium: [
      { icon: Crown, title: "Priority Queue", desc: "Skip the line during peak hours" },
      { icon: Gift, title: "Loyalty Rewards", desc: "Earn points on every service" },
      { icon: Award, title: "VIP Support", desc: "Dedicated 24/7 concierge line" },
      { icon: Sparkles, title: "AI Predictions", desc: "Predictive maintenance alerts" },
    ]
  };

  const reviews = [
    { name: "Michael R.", rating: 5, text: "Saved me at 2 AM on the highway. Provider arrived in 15 minutes!", date: "2 days ago", verified: true },
    { name: "Sarah K.", rating: 5, text: "The AR scanner diagnosed my battery issue instantly. Amazing tech!", date: "1 week ago", verified: true },
    { name: "James L.", rating: 5, text: "Best roadside app ever. The offline mode is a lifesaver.", date: "2 weeks ago", verified: true },
    { name: "Emily W.", rating: 4, text: "Great service, love the fingerprint login feature.", date: "3 weeks ago", verified: true },
  ];

  const screenshots = [
    { title: "Home", gradient: "from-blue-600 to-blue-800" },
    { title: "Request Help", gradient: "from-red-500 to-orange-500" },
    { title: "Live Tracking", gradient: "from-green-500 to-emerald-600" },
    { title: "AR Scanner", gradient: "from-purple-500 to-pink-500" },
    { title: "Marketplace", gradient: "from-amber-500 to-yellow-500" },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="app-store-listing">
      {/* Store Selector */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('playstore')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                activeTab === 'playstore' 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/25' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <PlayCircle className="w-5 h-5" />
              Google Play
            </button>
            <button
              onClick={() => setActiveTab('appstore')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                activeTab === 'appstore' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Apple className="w-5 h-5" />
              App Store
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* App Header */}
        <div className="flex gap-4 mb-6">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-[22px] bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 flex items-center justify-center shadow-2xl shadow-blue-500/30 flex-shrink-0">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-black text-white">J</div>
              <div className="text-[8px] sm:text-[10px] font-bold text-blue-200 -mt-1">MOBILE</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white truncate">Jiffys Mobile</h1>
            <p className="text-sm text-blue-400 font-medium">Jiffys Assistance LLC</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-white">4.9</span>
                <span className="text-gray-400 text-sm">(12.4K)</span>
              </div>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                Editors' Choice
              </Badge>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {activeTab === 'playstore' ? '1M+ downloads' : '#1 in Auto & Vehicles'}
            </p>
          </div>
        </div>

        {/* Install Button */}
        <div className="mb-8">
          {isInstalled ? (
            <Button className="w-full py-6 text-lg font-bold bg-gray-700 text-gray-300 cursor-default" disabled>
              <Check className="w-5 h-5 mr-2" />
              Installed
            </Button>
          ) : (
            <Button 
              className={`w-full py-6 text-lg font-bold ${
                activeTab === 'playstore'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
              } shadow-xl`}
              onClick={() => setShowInstallPrompt(true)}
            >
              <Download className="w-5 h-5 mr-2" />
              Install - Free
            </Button>
          )}
          <p className="text-center text-xs text-gray-500 mt-2">
            Contains ads · In-app purchases
          </p>
        </div>

        {/* Screenshots */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">Screenshots</h2>
          <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
            {screenshots.map((screen, i) => (
              <div 
                key={screen.title || i}
                className={`flex-shrink-0 w-32 h-56 sm:w-40 sm:h-72 rounded-2xl bg-gradient-to-br ${screen.gradient} flex items-center justify-center shadow-xl`}
              >
                <div className="text-center text-white/90">
                  <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-xs font-medium">{screen.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* App vs Web Comparison */}
        <div className="mb-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-5 border border-gray-700">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            App Exclusive Features
          </h2>
          <p className="text-sm text-gray-400 mb-4">Features only available in the mobile app:</p>
          
          <div className="grid grid-cols-2 gap-3">
            {appFeatures.exclusive.map((feature, i) => (
              <div key={feature.title || i} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-xl">
                <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-medium text-sm text-white truncate">{feature.title}</h4>
                  <p className="text-xs text-gray-400 line-clamp-2">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Premium Features */}
        <div className="mb-8 bg-gradient-to-br from-amber-900/30 to-yellow-900/20 rounded-2xl p-5 border border-yellow-600/30">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            Jiffys Premium
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs ml-auto">
              $4.99/mo
            </Badge>
          </h2>
          
          <div className="space-y-3">
            {appFeatures.premium.map((feature, i) => (
              <div key={feature.title || i} className="flex items-center gap-3 p-3 bg-yellow-500/10 rounded-xl">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <feature.icon className="w-4 h-4 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm text-white">{feature.title}</h4>
                  <p className="text-xs text-gray-400">{feature.desc}</p>
                </div>
                <Check className="w-4 h-4 text-yellow-400" />
              </div>
            ))}
          </div>
          
          <Button className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold">
            Start 7-Day Free Trial
          </Button>
        </div>

        {/* Web vs App Comparison Table */}
        <div className="mb-8 bg-gray-800/50 rounded-2xl p-5 border border-gray-700">
          <h2 className="text-lg font-bold mb-4">App vs Web Comparison</h2>
          
          <div className="space-y-2">
            {[
              { feature: "Biometric Login", app: true, web: false },
              { feature: "Offline Access", app: true, web: false },
              { feature: "Push Notifications", app: true, web: false },
              { feature: "AR Vehicle Scanner", app: true, web: false },
              { feature: "Background Location", app: true, web: false },
              { feature: "Haptic Feedback", app: true, web: false },
              { feature: "Home Screen Widgets", app: true, web: false },
              { feature: "Quick Actions", app: true, web: false },
              { feature: "Priority Support Queue", app: true, web: false },
              { feature: "Loyalty Rewards", app: true, web: false },
              { feature: "Basic Service Request", app: true, web: true },
              { feature: "Live Tracking", app: true, web: true },
              { feature: "Marketplace", app: true, web: true },
              { feature: "Payment", app: true, web: true },
            ].map((row, i) => (
              <div key={row.feature || i} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                <span className="text-sm text-gray-300">{row.feature}</span>
                <div className="flex gap-6">
                  <div className="w-12 text-center">
                    {row.app ? (
                      <Check className="w-5 h-5 text-green-400 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-gray-600 mx-auto" />
                    )}
                  </div>
                  <div className="w-12 text-center">
                    {row.web ? (
                      <Check className="w-5 h-5 text-green-400 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-gray-600 mx-auto" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
              <span></span>
              <div className="flex gap-6">
                <span className="w-12 text-center">App</span>
                <span className="w-12 text-center">Web</span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Ratings & Reviews</h2>
            <Button variant="ghost" size="sm" className="text-blue-400">
              See all <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="flex items-center gap-6 mb-6 p-4 bg-gray-800/50 rounded-xl">
            <div className="text-center">
              <div className="text-5xl font-bold text-white">4.9</div>
              <div className="flex gap-0.5 justify-center mt-1">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">12.4K ratings</p>
            </div>
            <div className="flex-1 space-y-1">
              {[5,4,3,2,1].map(rating => (
                <div key={rating} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-3">{rating}</span>
                  <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: rating === 5 ? '85%' : rating === 4 ? '10%' : '2%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-4">
            {reviews.map((review, i) => (
              <div key={`${review.name}-${review.date}` || i} className="p-4 bg-gray-800/30 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-sm font-bold">
                      {review.name[0]}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-white">{review.name}</p>
                      <p className="text-xs text-gray-500">{review.date}</p>
                    </div>
                  </div>
                  {review.verified && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                      Verified
                    </Badge>
                  )}
                </div>
                <div className="flex gap-0.5 mb-2">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-300">{review.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* App Info */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">About this app</h2>
          <p className="text-sm text-gray-300 mb-4">
            Jiffys Mobile is your ultimate roadside companion. Whether you're stranded with a flat tire, 
            dead battery, or need a mobile mechanic, help is just a tap away. Our network of certified 
            technicians provides 24/7 service across Fort Wayne and surrounding areas.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Version</p>
              <p className="text-white">2.3.0</p>
            </div>
            <div>
              <p className="text-gray-500">Size</p>
              <p className="text-white">45 MB</p>
            </div>
            <div>
              <p className="text-gray-500">Released</p>
              <p className="text-white">Dec 27, 2025</p>
            </div>
            <div>
              <p className="text-gray-500">Developer</p>
              <p className="text-white">Jiffys Assistance LLC</p>
            </div>
          </div>
        </div>

        {/* Data Safety */}
        <div className="mb-8 p-4 bg-gray-800/30 rounded-xl border border-gray-700">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            Data safety
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Safety starts with understanding how developers collect and share your data.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-300">Data is encrypted in transit</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-300">You can request that data be deleted</p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-300">Independent security review</p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center text-sm text-gray-500 pb-8">
          <p>Jiffys Assistance LLC</p>
          <p>Fort Wayne, IN 46802</p>
          <p className="text-blue-400">support@jiffysassistance.site</p>
          <p className="mt-2">(260) 218-3536</p>
        </div>
      </div>

      {/* Install Prompt Modal */}
      {showInstallPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Install Jiffys Mobile</h3>
              <button onClick={() => setShowInstallPrompt(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-800 rounded-xl">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center">
                <span className="text-2xl font-black text-white">J</span>
              </div>
              <div>
                <p className="font-bold text-white">Jiffys Mobile</p>
                <p className="text-sm text-gray-400">jiffysassistance.site</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-300">Add to your home screen for:</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Instant access with one tap
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Full-screen app experience
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Push notifications
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Works offline
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full py-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 font-bold"
                onClick={() => {
                  // Trigger PWA install
                  if (window.deferredPrompt) {
                    window.deferredPrompt.prompt();
                  }
                  setShowInstallPrompt(false);
                }}
              >
                <Download className="w-5 h-5 mr-2" />
                Install App
              </Button>
              <Button 
                variant="ghost" 
                className="w-full text-gray-400"
                onClick={() => setShowInstallPrompt(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppStoreListing;
